import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';
import Icon from '../../components/AppIcon';

const CustomerPortalLogin = () => {
  const navigate = useNavigate();
  const { requestOtp, verifyOtp } = useCustomerPortal();

  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState('');

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    const result = await requestOtp(email?.trim());
    setLoading(false);
    if (result?.error) {
      setError(result?.error);
      return;
    }
    if (result?.devOtp) {
      setDevOtp(result?.devOtp);
    }
    setStep('otp');
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    setError('');
    if (!otp?.trim()) {
      setError('Please enter your verification code.');
      return;
    }
    setLoading(true);
    const result = await verifyOtp(email?.trim(), otp?.trim());
    setLoading(false);
    if (result?.error) {
      setError(result?.error);
      return;
    }
    navigate('/customer-portal');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon name="LayoutDashboard" size={32} color="var(--color-primary)" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Customer Portal</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {step === 'email' ?'Enter your email to access your portal'
              : `We sent a code to ${email}`}
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-elevation-2 border border-border p-8">
          {step === 'email' ? (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="you@company.com"
                  autoFocus
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-lg px-3 py-2.5">
                  <Icon name="AlertCircle" size={15} color="currentColor" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
                    Sending code…
                  </>
                ) : (
                  <>
                    <Icon name="Mail" size={16} color="currentColor" />
                    Send Verification Code
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                  placeholder="6-digit code"
                  autoFocus
                  maxLength={6}
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors text-sm tracking-widest text-center text-lg font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Enter the 6-digit code sent to your email. Code expires in 15 minutes.
                </p>
              </div>

              {/* Dev mode: show OTP inline */}
              {devOtp && (
                <div className="flex items-center gap-2 text-sm text-warning bg-warning/10 rounded-lg px-3 py-2.5">
                  <Icon name="Info" size={15} color="currentColor" />
                  <span>Dev mode — your code: <strong className="font-mono">{devOtp}</strong></span>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-lg px-3 py-2.5">
                  <Icon name="AlertCircle" size={15} color="currentColor" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp?.length < 6}
                className="w-full bg-primary text-primary-foreground rounded-lg py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
                    Verifying…
                  </>
                ) : (
                  <>
                    <Icon name="LogIn" size={16} color="currentColor" />
                    Access Portal
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setOtp(''); setError(''); setDevOtp(''); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              >
                ← Use a different email
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          This portal is for customers only.{' '}
          <a href="/login" className="text-primary hover:underline">
            Staff login →
          </a>
        </p>
      </div>
    </div>
  );
};

export default CustomerPortalLogin;
